import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  contentChild,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  HeaderTools,
  HeaderToolsService,
} from 'src/app/core/services/header-tools.service';
import { GridDetailPaneComponent } from './grid-detail-pane.component';

/**
 * The grid's Data View shell (WAP-1730): the fixed-width side region projected
 * by the host into `<app-data-grid>`'s `[sideRegion]` slot, holding the view's
 * own widgets (`@if (dataView.open())` around them in the host, so a closed
 * pane tears its queries down) and, on master-detail hosts, the
 * `app-grid-detail-pane` overlay riding in the same slot - its presence keeps
 * the region rendered while the pane is closed (WAP-1733). Owns the open state
 * and registers the header's
 * Data View toggle (plus the shared date-range picker when the host passes
 * `dateRange`) - `HeaderToolsService.set()` replaces the whole tools object, so
 * one registrar. The host persists `open()` through its view-preset settings
 * and restores it with `setOpen()`. The refit after a toggle is the wrapper's
 * ResizeObserver (WAP-1726). Also owns which widgets are shown (WAP-1732): the
 * pane's content component reads `widgetVisibility()` / `isWidgetVisible()`
 * and calls `toggleWidget()` through DI, the host persists the map in its
 * view-preset settings and restores it with `setWidgetVisibility()`; a widget
 * the map does not name is visible.
 */
@Component({
  selector: 'app-grid-data-view',
  templateUrl: './grid-data-view.component.html',
  styleUrls: ['./grid-data-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: { '[class.grid-data-view--collapsed]': 'collapsed()' },
})
export class GridDataViewComponent {
  /** Shared date-range picker for time-series views (the log explorers); `null` for snapshot views. */
  readonly dateRange = input<HeaderTools['dateRange'] | null>(null);
  /** A master-detail host's row-detail overlay, projected into the same slot. */
  private readonly detailPane = contentChild(GridDetailPaneComponent);

  private readonly headerTools = inject(HeaderToolsService);
  private readonly isOpen = signal(true);
  private readonly open$ = toObservable(this.isOpen);

  /** `true` while the Data View pane is open. */
  readonly open = this.isOpen.asReadonly();

  private readonly visibility = signal<{ [widgetId: string]: boolean }>({});
  /** Widget id -> shown; an absent id is shown. Rides along in the host's view presets. */
  readonly widgetVisibility = this.visibility.asReadonly();
  protected readonly collapsed = computed(
    () => !this.isOpen() && !this.detailPane(),
  );

  constructor() {
    effect(() => {
      const dateRange = this.dateRange();
      this.headerTools.set({
        ...(dateRange ? { dateRange } : {}),
        dataView: { open$: this.open$, toggle: () => this.toggle() },
      });
    });
    inject(DestroyRef).onDestroy(() => this.headerTools.clear());
  }

  toggle(): void {
    this.isOpen.update((open) => !open);
  }

  /** The pane's own close button. */
  close(): void {
    this.isOpen.set(false);
  }

  /** A view preset or the remembered view settings restoring the pane state. */
  setOpen(open: boolean): void {
    this.isOpen.set(open);
  }

  isWidgetVisible(id: string): boolean {
    return this.visibility()[id] ?? true;
  }

  /** The widget's own show/hide toggle in the pane. */
  toggleWidget(id: string): void {
    this.visibility.update((map) => ({ ...map, [id]: !(map[id] ?? true) }));
  }

  /** A view preset being applied: the saved selection replaces the current one. */
  setWidgetVisibility(widgetVisibility: { [widgetId: string]: boolean }): void {
    this.visibility.set({ ...widgetVisibility });
  }
}
